import type {FillObject, FillTypes, GradientFillObject} from '@PosterWhiteboard/classes/fill.types';
import {createSolidFill, isGradientFillObject, withFillColors} from '@PosterWhiteboard/classes/fill.types';
import type {TextRunFillGradientObject, TextSelectionStylesSummary} from '@PosterWhiteboard/items/text-item/text-style-runs.types';
import type {ShapeItem} from '@PosterWhiteboard/items/shape-item/shape-item.class';
import {FillSurface, withFillType} from '@PosterWhiteboard/classes/fill-surface';
import type {RGBA} from '@Utils/color.util';
import type {ItemObject, ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import type {SubtitleObject} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle.types';
import type {DeepPartial} from '@/global';

/**
 * Item writes that replace one of the item's fills with a fill built from the one it has now and the surface it paints. A fill is written
 * whole, so a write to several items builds each item's fill from that item's own fill, keeping its own type and layout. Each returns null
 * for an item without that fill.
 */
export type GetNewFill = (fill: FillObject, surface: FillSurface) => FillObject;

export const getTextFillChange = (item: ItemType, getNewFill: GetNewFill): DeepPartial<ItemObject> | null => {
  return 'textStyles' in item ? {textStyles: {fill: getNewFill(item.textStyles.fill.toObject(), FillSurface.TEXT)}} : null;
};

export const getTextBackgroundFillChange = (item: ItemType, getNewFill: GetNewFill): DeepPartial<ItemObject> | null => {
  return 'background' in item && 'fill' in item.background ? {background: {fill: getNewFill(item.background.fill.toObject(), FillSurface.TEXT_BACKGROUND)}} : null;
};

export const getShapeFillChange = (item: ItemType, getNewFill: GetNewFill): DeepPartial<ItemObject> | null => {
  return item.hasShapeFill() ? {fill: getNewFill(item.getFillObject(), item.getFillSurface())} : null;
};

/** The shape's fill as a fill of `fillType` painting `colors`, keeping its layout. */
export const getShapeFillWithTypeAndColors = (item: ShapeItem, fillType: FillTypes, colors: RGBA[]): FillObject => {
  return withFillColors(withFillType(item.getFillObject(), fillType, item.getFillSurface()), colors);
};

/** A color edit on a group of shapes gives every shape the edited fill's type as well as its colors. */
export const getShapeFillTypeAndColorsChange = (item: ItemType, fillType: FillTypes, colors: RGBA[]): DeepPartial<ItemObject> | null => {
  return item.hasShapeFill() ? {fill: getShapeFillWithTypeAndColors(item, fillType, colors)} : null;
};

/** A QR code paints its dots with one fill and the square behind them with another. */
export type QRFillKey = 'foregroundFill' | 'backgroundFill';

export const getQRFillChange = (item: ItemType, fillKey: QRFillKey, getNewFill: GetNewFill): DeepPartial<ItemObject> | null => {
  return item.isQRItem() ? {[fillKey]: getNewFill(item[fillKey].toObject(), FillSurface.QR)} : null;
};

/** The two text styles every subtitle of a transcript carries: the sentence, and the word being spoken. */
export type SubtitleTextStylesKey = 'sentenceTextStyles' | 'activeWordTextStyles';

/** Every subtitle of a transcript shares one style, so the new fill is built from any of them and written to all. */
export const getSubtitleTextFillChange = (transcriptItem: TranscriptItem, textStylesKey: SubtitleTextStylesKey, getNewFill: GetNewFill): DeepPartial<SubtitleObject> => {
  return {[textStylesKey]: {fill: getNewFill(transcriptItem.getAnySubtitle()[textStylesKey].fill.toObject(), FillSurface.TEXT)}};
};

export const getSubtitleBackgroundFillChange = (transcriptItem: TranscriptItem, getNewFill: GetNewFill): DeepPartial<SubtitleObject> => {
  return {backgroundFill: getNewFill(transcriptItem.getAnySubtitle().backgroundFill.toObject(), FillSurface.TEXT)};
};

/** The gradient a text selection switches to: the selection's own gradient as the new type, or a new gradient from its solid color. */
export const getRunFillGradientForType = (
  selectionFillGradient: TextRunFillGradientObject | null,
  solidColor: RGBA,
  newType: GradientFillObject['fillType']
): TextRunFillGradientObject => {
  const fill = withFillType(selectionFillGradient ?? createSolidFill(solidColor), newType, FillSurface.TEXT);
  if (!isGradientFillObject(fill)) {
    throw new Error(`Switching a text selection's fill to ${newType} gave a ${fill.fillType} fill`);
  }
  return fill;
};

/** The fill a text fill picker shows: while editing, the selection's gradient or solid color; otherwise the whole box's fill. */
export const getDisplayedTextFill = (selectionStyles: TextSelectionStylesSummary | null, boxFill: FillObject): FillObject => {
  if (selectionStyles === null) {
    return boxFill;
  }
  if (selectionStyles.fillGradient) {
    return selectionStyles.fillGradient;
  }
  return selectionStyles.fillColor ? createSolidFill(selectionStyles.fillColor) : boxFill;
};
